import { useState, useEffect } from 'react';
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import { updateOnboardingStep } from '../../functions/updateOnboarding';
import TextField from "@mui/material/TextField";
import * as theme from '../../theme';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import ErrorDisplay from '../../components/ErrorDisplay';
import FormControl from '@mui/material/FormControl';
import { InputLabel, Select, MenuItem } from '@mui/material';
import AddableList from '../../components/AddableList';
import ConfirmationModal from '../../components/ConfirmationModal';
import Grid from '@mui/material/Grid';
import { TherapiesContext } from '../../contexts/TherapiesContext';

export default function OnboardingForm() {
  const [formData, setFormData] = useState({
    clientCount: '',
    monthCount: '',
    supplementCompanies: [],
    conditions: [],
    leadsGroupDetox: null,
    groupDetox: null,
    specificTherapies: [], // Array of objects
    additionalTherapies: [],
    scanningRequests: [],
    specialRequests: [],
    questions: [],
    detoxCohorts: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    if (id != null) {
      getPractitioner();
    }
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBooleanChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const addItem = (field, value) => {
    const isObjectField = field === 'supplementCompanies' || field === 'specificTherapies' || field === 'additionalTherapies';
    const newValue = isObjectField ? value : value.name || value;
    setFormData({
      ...formData,
      [field]: [...formData[field], newValue]
    });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const handleTherapyToggle = (therapy) => {
    const isSelected = formData.specificTherapies.some(t => t.name === therapy);
    if (isSelected) {
      setFormData({
        ...formData,
        specificTherapies: formData.specificTherapies.filter(t => t.name !== therapy)
      });
    } else {
      setFormData({
        ...formData,
        specificTherapies: [...formData.specificTherapies, { name: therapy }]
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  const confirmSave = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError("");

    try {
      // Clean formData to remove null/undefined values as a safeguard
      const cleanedFormData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          cleanedFormData[key] = formData[key];
        }
      });

      const { success } = await updateOnboardingStep({
        practitionerId: id,
        step: 'form',
        formData: cleanedFormData,
      });

      if (success) {
        navigateBack();
      } else {
        throw new Error('Failed to update onboarding step');
      }
    } catch (err) {
      setError("Failed to save onboarding form: " + err.message);
    }

    setLoading(false);
  };

  function navigateBack() {
    navigate(`/dashboard`);
  }

  async function getPractitioner() {
    try {
      const practitionerRef = doc(db, "practitioners", id);
      const docSnap = await getDoc(practitionerRef);
      const practitionerData = docSnap.data();
      if (practitionerData?.onboarding?.form) {
        const form = practitionerData.onboarding.form;
        let supps = form.supplementCompanies || [];
        if (typeof supps === 'string') {
          supps = supps.split(',').map(s => ({ name: s.trim() })).filter(s => s.name);
                }
        let conds = form.conditions || [];
        if (typeof conds === 'string') {
          conds = conds.split(',').map(s => s.trim()).filter(Boolean);
        }
        let addTherapies = form.additionalTherapies || [];
        if (typeof addTherapies === 'string') {
          addTherapies = addTherapies.split(',').map(s => ({ name: s.trim() })).filter(s => s.name);
        } else {
          addTherapies = addTherapies.map(t => ({ name: t.name || t }));
        }
        let scanReqs = form.scanningRequests || [];
        if (typeof scanReqs === 'string') {
          scanReqs = scanReqs.split(',').map(s => s.trim()).filter(Boolean);
        }
        let specReqs = form.specialRequests || [];
        if (typeof specReqs === 'string') {
          specReqs = specReqs.split(',').map(s => s.trim()).filter(Boolean);
        }
        let qs = form.questions || [];
        if (typeof qs === 'string') {
          qs = qs.split(',').map(s => s.trim()).filter(Boolean);
        }
        let detoxCohs = form.detoxCohorts || [];
        if (typeof detoxCohs === 'string') {
          detoxCohs = detoxCohs.split(',').map(s => s.trim()).filter(Boolean);
        }
        let specTherapies = form.specificTherapies || [];
        if (typeof specTherapies === 'string') {
          specTherapies = specTherapies.split(',').map(s => ({ name: s.trim() })).filter(s => s.name);
        } else {
          specTherapies = specTherapies.map(t => ({ name: t.name || t }));
        }
        setFormData({
          ...formData,
          ...form,
          supplementCompanies: supps,
          conditions: conds,
          additionalTherapies: addTherapies,
          scanningRequests: scanReqs,
          specialRequests: specReqs,
          questions: qs,
          detoxCohorts: detoxCohs,
          specificTherapies: specTherapies,
          leadsGroupDetox: form.leadsGroupDetox ?? false, // Default to false
          groupDetox: form.groupDetox ?? false // Default to false
        });
      }
    } catch (err) {
      console.log("Failed to get practitioner: " + err.message);
    }
  }

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>Practitioner Onboarding Form</div>
      <div style={theme.SubHeadingText}>
        Fill out the form below to prepare us for our onboarding call.
      </div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 8 }}
        >
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>How many clients do you see weekly?</div>
            <TextField
              label={'Clients Per Week'}
              name="clientCount"
              value={formData.clientCount}
              onChange={handleChange}
              variant="filled"
              disabled={loading}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>How many clients per month do you anticipate completing a scan?</div>
            <TextField
              label={'Client Scans Per Month'}
              name="monthCount"
              value={formData.monthCount}
              onChange={handleChange}
              variant="filled"
              disabled={loading}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>List all supplement companies you use (if only using a single product from a company, just list that product instead of the company):</div>
            <AddableList
              label="New Supplement Company"
              items={formData.supplementCompanies}
              onAdd={(value) => addItem('supplementCompanies', value)}
              onRemove={(index) => removeItem('supplementCompanies', index)}
              disabled={loading}
              isObject={true}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>What types of conditions/health concerns do you work with?</div>
            <AddableList
              label="New Condition"
              items={formData.conditions}
              onAdd={(value) => addItem('conditions', value)}
              onRemove={(index) => removeItem('conditions', index)}
              disabled={loading}
              isObject={false}
            />
          </Box>
          <Box sx={{ border: 4, borderColor: theme.Colors.GoldDark, p: 2, borderRadius: 1 }}>
            <FormControl variant="filled" fullWidth>
              <InputLabel>Do you lead group detox programs/cohorts?</InputLabel>
              <Select
                name="leadsGroupDetox"
                value={formData.leadsGroupDetox === null ? '' : formData.leadsGroupDetox}
                onChange={(e) => handleBooleanChange('leadsGroupDetox', e.target.value === 'true')}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            {formData.leadsGroupDetox === true && (
              <FormControl variant="filled" fullWidth sx={{ marginTop: 2 }}>
                <InputLabel>Would you want to include this in the program?</InputLabel>
                <Select
                  name="groupDetox"
                  value={formData.groupDetox === null ? '' : formData.groupDetox}
                  onChange={(e) => handleBooleanChange('groupDetox', e.target.value === 'true')}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            )}
            {formData.leadsGroupDetox === true && formData.groupDetox === true && (
              <Box sx={{ ...theme.FullForm.form, gap: 2, marginTop: 2 }}>
                <div style={{ paddingLeft: 6 }}>What type of detox cohort(s) do you provide?</div>
                <AddableList
                  label="New Detox Cohort"
                  items={formData.detoxCohorts}
                  onAdd={(value) => addItem('detoxCohorts', value)}
                  onRemove={(index) => removeItem('detoxCohorts', index)}
                  disabled={loading}
                  isObject={false}
                />
              </Box>
            )}
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>
              We can scan to see what additional therapies or treatments are most beneficial for your client. <b>Select from this list any therapies you want included in your scans.</b>
            </div>
            <Grid container spacing={1}>
              {TherapiesContext.map((therapy) => (
                <Grid item xs={12} sm={6} md={4} key={therapy}>
                  <Button
                    variant={formData.specificTherapies.some(t => t.name === therapy) ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => handleTherapyToggle(therapy)}
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      width: '100%',
                      fontSize: 12,
                      backgroundColor: formData.specificTherapies.some(t => t.name === therapy)
                        ? theme.Colors.Purple
                        : 'transparent',
                      color: formData.specificTherapies.some(t => t.name === therapy)
                        ? '#fff'
                        : theme.Colors.GrayDark,
                      borderColor: theme.Colors.GrayMedium,
                      '&:hover': {
                        backgroundColor: formData.specificTherapies.some(t => t.name === therapy)
                          ? theme.Colors.PurpleDark
                          : theme.Colors.GrayLight,
                      },
                    }}
                  >
                    {therapy}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>Are there specific therapies that you use at your clinic or refer people out to that are not included in the list above. If so, include in the list below.</div>
            <AddableList
              label="New Additional Therapy"
              items={formData.additionalTherapies}
              onAdd={(value) => addItem('additionalTherapies', value)}
              onRemove={(index) => removeItem('additionalTherapies', index)}
              disabled={loading}
              isObject={true}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>Our goal is to customize the scans to your clinic's needs as much as possible. Are there any special scanning requests or things you would like tested for your clients?</div>
            <AddableList
              label="New Scanning Request"
              items={formData.scanningRequests}
              onAdd={(value) => addItem('scanningRequests', value)}
              onRemove={(index) => removeItem('scanningRequests', index)}
              disabled={loading}
              isObject={false}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>Are there any special requests in regards to the report your clients will receive?</div>
            <AddableList
              label="New Report Request"
              items={formData.specialRequests}
              onAdd={(value) => addItem('specialRequests', value)}
              onRemove={(index) => removeItem('specialRequests', index)}
              disabled={loading}
              isObject={false}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2 }}>
            <div style={{ paddingLeft: 6 }}>Specific questions you have for TruBalance?</div>
            <AddableList
              label="New Question"
              items={formData.questions}
              onAdd={(value) => addItem('questions', value)}
              onRemove={(index) => removeItem('questions', index)}
              disabled={loading}
              isObject={false}
            />
          </Box>
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigateBack()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmSave}
        title="Confirm Form Submission"
        description="Are you sure you want to submit this form?"
        confirmButtonText="Save"
      />
    </div>
  );
}